import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveLocale, tryResolveSupportedLocale } from '../localization/translations';
import { normalizeChatConfiguration } from '../services/chatConfiguration';
import { ChatService } from '../services/chatService';
import type { ChatStreamChunk } from '../services/chatService';
import { FeedbackService } from '../services/feedbackService';
import type { AssistantImageMap, TaskPlanState, UserImageUpload, WidgetMessage } from '../types/chat';
import type { SubmitFeedbackInput, SubmitFeedbackRequest } from '../types/feedback';
import { clearAssistantImageCache, saveAssistantImage } from '../utils/assistantImageCache';
import { isWidgetDebugConsoleEnabled, setWidgetDebugConsoleEnabled } from '../utils/widgetDebugConsole';
import { emitAuthErrorEvent, isAuthErrorPayload, resolveAuthErrorMessage } from './useChatSession/auth';
import { copyTextToClipboard, createMessageId, createUuid, getTimestampMs } from './useChatSession/browserUtils';
import { parseDebugCommand, parseLanguageCommand, parseTokenCommand } from './useChatSession/commands';
import { applyLanguageToChatConfiguration, resolveEffectiveChatConfiguration } from './useChatSession/configuration';
import { ConversationResponseBuffer, formatConversationJsonResponses } from './useChatSession/streamFormatting';
import { getLastAssistantMessageIndex, getStatusDescription, mapUserImage, normalizeImageChunk, shouldLogStatusUpdate } from './useChatSession/streamHelpers';
import { emitTaskPlanEvent, mergeTaskPlanState, normalizeInjectedUserMessage, normalizeTaskPlanChunk, shouldClearTaskPlan } from './useChatSession/taskPlan';
import type { UseChatSessionOptions, UseChatSessionResult } from './useChatSession/types';

export type { UseChatSessionOptions, UseChatSessionResult } from './useChatSession/types';
export { formatConversationJsonResponses } from './useChatSession/streamFormatting';
export { parseDebugCommand, parseTokenCommand } from './useChatSession/commands';
export { resolveEffectiveChatConfiguration } from './useChatSession/configuration';

type SendMessageOptions = {
    userImages?: UserImageUpload[];
    clearDraftAfterSend?: boolean;
};

export function useChatSession(options: UseChatSessionOptions): UseChatSessionResult {
    const { chat, configuration, language, onLanguageOverride } = options;
    const chatConfiguration = useMemo(() => configuration ?? normalizeChatConfiguration(chat), [configuration, chat]);
    const normalizedLanguage = useMemo(() => resolveLocale(language), [language]);
    const [languageOverride, setLanguageOverride] = useState<string>('');
    const activeLanguage = useMemo(
        () => (languageOverride.length > 0 ? languageOverride : normalizedLanguage),
        [languageOverride, normalizedLanguage]
    );
    const [authOverride, setAuthOverride] = useState<string>('');

    useEffect(() => {
        setLanguageOverride('');
    }, [normalizedLanguage]);

    const chatConfigurationWithLanguage = useMemo(
        () => applyLanguageToChatConfiguration(chatConfiguration, activeLanguage),
        [activeLanguage, chatConfiguration]
    );
    const effectiveChatConfiguration = useMemo(
        () => resolveEffectiveChatConfiguration(chatConfigurationWithLanguage, authOverride),
        [authOverride, chatConfigurationWithLanguage]
    );
    const chatService = useMemo(
        () => (effectiveChatConfiguration ? new ChatService(effectiveChatConfiguration) : null),
        [effectiveChatConfiguration]
    );
    const feedbackService = useMemo(
        () => (effectiveChatConfiguration ? new FeedbackService(effectiveChatConfiguration) : null),
        [effectiveChatConfiguration]
    );
    const [draftMessage, setDraftMessage] = useState('');
    const [threadId, setThreadId] = useState<string | null>(null);
    const [pendingStatus, setPendingStatus] = useState<string>('');
    const [hasShownAuthError, setHasShownAuthError] = useState(false);

    const initialMessages = useMemo<WidgetMessage[]>(() => [], []);

    const [messages, setMessages] = useState<WidgetMessage[]>(initialMessages);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [images, setImages] = useState<AssistantImageMap>({});
    const [taskPlan, setTaskPlan] = useState<TaskPlanState | null>(null);
    const [isDebugConsoleEnabled, setIsDebugConsoleEnabled] = useState(isWidgetDebugConsoleEnabled);
    const [conversationResponseCount, setConversationResponseCount] = useState(0);
    const abortControllerRef = useRef<AbortController | null>(null);
    const isRequestInFlightRef = useRef(false);
    const lastLoggedStatusRef = useRef<string>('');
    const debugConsoleEnabledRef = useRef(isDebugConsoleEnabled);
    const conversationResponseBufferRef = useRef(new ConversationResponseBuffer());

    useEffect(() => {
        debugConsoleEnabledRef.current = isDebugConsoleEnabled;
    }, [isDebugConsoleEnabled]);

    useEffect(() => {
        setMessages(initialMessages);
        setImages({});
        setTaskPlan(null);
        setIsSubmittingFeedback(false);
        setConversationResponseCount(0);
        conversationResponseBufferRef.current.clear();
        clearAssistantImageCache();
    }, [initialMessages]);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        abortControllerRef.current?.abort();
        setThreadId(null);
        setPendingStatus('');
        isRequestInFlightRef.current = false;
        lastLoggedStatusRef.current = '';
        setHasShownAuthError(false);
        setImages({});
        setTaskPlan(null);
        setIsSubmittingFeedback(false);
        setConversationResponseCount(0);
        conversationResponseBufferRef.current.clear();
        clearAssistantImageCache();
    }, [chatService]);

    const updateLatestAssistantMessage = useCallback((updates: Partial<WidgetMessage>) => {
        setMessages((previous) => {
            if (!previous.length) {
                return previous;
            }

            const next = [...previous];
            const lastIndex = next.length - 1;
            const lastMessage = next[lastIndex];

            if (lastMessage.role !== 'assistant') {
                return previous;
            }

            next[lastIndex] = {
                ...lastMessage,
                ...updates
            };

            return next;
        });
    }, []);

    const insertAuthErrorMessage = useCallback(
        (message: string) => {
            if (!message.trim().length) {
                return;
            }

            const authMessage: WidgetMessage = {
                id: createMessageId('assistant-auth'),
                role: 'assistant',
                content: message,
                isAuthError: true,
                feedbackSubmitted: true
            };

            setMessages((previous) => {
                if (!previous.length) {
                    return [authMessage];
                }

                const lastAssistantIndex = getLastAssistantMessageIndex(previous);
                if (lastAssistantIndex < 0) {
                    return [...previous, authMessage];
                }

                const next = [...previous];
                next.splice(lastAssistantIndex, 0, authMessage);
                return next;
            });
        },
        [setMessages]
    );

    const sendPreparedMessage = useCallback(async (message: string, options?: SendMessageOptions): Promise<boolean> => {
        if (isRequestInFlightRef.current) {
            return false;
        }

        const trimmedMessage = message.trim();
        const userImages = options?.userImages;
        const clearDraftAfterSend = options?.clearDraftAfterSend ?? false;
        const clearDraftMessage = () => {
            if (clearDraftAfterSend) {
                setDraftMessage('');
            }
        };
        const hasImages = Array.isArray(userImages) && userImages.length > 0;

        if (!trimmedMessage.length && !hasImages) {
            return false;
        }

        const tokenCommand = parseTokenCommand(trimmedMessage);
        if (tokenCommand !== null) {
            const token = tokenCommand;
            setAuthOverride(token);
            setMessages((previous) => [
                ...previous,
                {
                    id: createMessageId('assistant'),
                    role: 'assistant',
                    content: token.length > 0 ? 'Token updated for this session.' : 'Token cleared for this session.',
                    feedbackSubmitted: true
                }
            ]);
            clearDraftMessage();
            setPendingStatus('');
            return true;
        }

        const debugCommand = parseDebugCommand(trimmedMessage);
        if (debugCommand !== null) {
            setIsDebugConsoleEnabled(debugCommand);
            setWidgetDebugConsoleEnabled(debugCommand);
            setMessages((previous) => [
                ...previous,
                {
                    id: createMessageId('assistant'),
                    role: 'assistant',
                    content: debugCommand
                        ? 'Debug console output enabled for this session.'
                        : 'Debug console output disabled for this session.',
                    feedbackSubmitted: true
                }
            ]);
            clearDraftMessage();
            setPendingStatus('');
            return true;
        }

        const requestedLanguage = parseLanguageCommand(trimmedMessage);
        if (requestedLanguage !== null) {
            if (!requestedLanguage.length) {
                setMessages((previous) => [
                    ...previous,
                    {
                        id: createMessageId('assistant'),
                        role: 'assistant',
                        content: 'Usage: /lang <language>. Example: /lang en-GB or /lang en.',
                        feedbackSubmitted: true
                    }
                ]);
                clearDraftMessage();
                setPendingStatus('');
                return true;
            }

            const resolvedLanguage = tryResolveSupportedLocale(requestedLanguage);
            if (!resolvedLanguage) {
                setMessages((previous) => [
                    ...previous,
                    {
                        id: createMessageId('assistant'),
                        role: 'assistant',
                        content: `Language "${requestedLanguage}" is not supported in this widget build.`,
                        feedbackSubmitted: true
                    }
                ]);
                clearDraftMessage();
                setPendingStatus('');
                return true;
            }

            if (resolvedLanguage === activeLanguage) {
                setMessages((previous) => [
                    ...previous,
                    {
                        id: createMessageId('assistant'),
                        role: 'assistant',
                        content: `Language is already set to ${resolvedLanguage}.`,
                        feedbackSubmitted: true
                    }
                ]);
                clearDraftMessage();
                setPendingStatus('');
                return true;
            }

            setLanguageOverride(resolvedLanguage);
            try {
                await onLanguageOverride?.(resolvedLanguage);
            } catch {
                setMessages((previous) => [
                    ...previous,
                    {
                        id: createMessageId('assistant'),
                        role: 'assistant',
                        content: `Language "${resolvedLanguage}" could not be applied.`,
                        feedbackSubmitted: true
                    }
                ]);
                clearDraftMessage();
                setPendingStatus('');
                return true;
            }
            setMessages((previous) => [
                ...previous,
                {
                    id: createMessageId('assistant'),
                    role: 'assistant',
                    content: `Language switched to ${resolvedLanguage} for this session.`,
                    feedbackSubmitted: true
                }
            ]);
            clearDraftMessage();
            setPendingStatus('');
            return true;
        }

        const userMessage: WidgetMessage = {
            id: createMessageId('user'),
            role: 'user',
            content: trimmedMessage
        };

        const assistantPlaceholder: WidgetMessage = {
            id: createMessageId('assistant'),
            role: 'assistant',
            content: '',
            feedbackSubmitted: false
        };

        setMessages((previous) => [...previous, userMessage, assistantPlaceholder]);
        conversationResponseBufferRef.current.addUserMessage(trimmedMessage);
        clearDraftMessage();
        setPendingStatus('');
        lastLoggedStatusRef.current = '';

        if (!chatService) {
            updateLatestAssistantMessage({ content: 'Connect a chat endpoint to send messages.' });
            return true;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        isRequestInFlightRef.current = true;
        setIsStreaming(true);
        const requestStart = getTimestampMs();
        let wasCanceled = false;
        let sawAuthError = false;

        try {
            let aggregatedResponse = '';
            const imagePayload = hasImages ? userImages!.map(mapUserImage) : undefined;

            for await (const chunk of chatService.stream({
                message: trimmedMessage,
                signal: controller.signal,
                threadId,
                images: imagePayload
            })) {
                conversationResponseBufferRef.current.add(chunk as ChatStreamChunk);
                setConversationResponseCount((previous) => previous + 1);

                if (chunk.threadId && chunk.threadId !== threadId) {
                    setThreadId(chunk.threadId);
                }

                if (chunk.contentType === 'status') {
                    const statusText = getStatusDescription(chunk.body);
                    if (statusText.length > 0) {
                        if (shouldLogStatusUpdate(lastLoggedStatusRef.current, statusText)) {
                            if (debugConsoleEnabledRef.current) {
                                console.info('[SKF Widget] Status update:', statusText);
                            }
                            lastLoggedStatusRef.current = statusText;
                        }
                        setPendingStatus(statusText);
                    }
                    continue;
                }

                if (chunk.contentType === 'task_plan') {
                    const plan = normalizeTaskPlanChunk(chunk.body);
                    if (plan) {
                        if (debugConsoleEnabledRef.current) {
                            const currentTaskSuffix = plan.currentTaskId ? `, current task ${plan.currentTaskId}` : '';
                            const eventLabel = plan.event ?? 'updated';
                            console.info(
                                `[SKF Widget] Task plan ${plan.planId}: ${eventLabel} (${plan.status})${currentTaskSuffix}`,
                                plan
                            );
                        }
                        setTaskPlan((previous) => {
                            const merged = mergeTaskPlanState(previous, plan);
                            if (shouldClearTaskPlan(merged, previous)) {
                                return null;
                            }

                            return merged;
                        });
                        emitTaskPlanEvent(plan);
                    }
                    continue;
                }

                if (chunk.contentType === 'user_message') {
                    const injected = normalizeInjectedUserMessage(chunk.body);
                    if (injected) {
                        aggregatedResponse = '';
                        setMessages((previous) => {
                            const next = [...previous];

                            while (next.length > 0) {
                                const last = next[next.length - 1];
                                const isEmptyAssistantPlaceholder = last.role === 'assistant' && !last.content.trim().length;
                                if (!isEmptyAssistantPlaceholder) {
                                    break;
                                }
                                next.pop();
                            }

                            if (next.length > 0) {
                                const last = next[next.length - 1];
                                if (last.role === 'user' && last.content === injected.content) {
                                    return next;
                                }
                            }

                            return [
                                ...next,
                                {
                                    id: createMessageId('user'),
                                    role: 'user',
                                    content: injected.content,
                                    isInjectedUserMessage: true
                                },
                                {
                                    id: createMessageId('assistant'),
                                    role: 'assistant',
                                    content: '',
                                    feedbackSubmitted: false
                                }
                            ];
                        });
                    }
                    continue;
                }

                if (chunk.contentType === 'image') {
                    const payload = normalizeImageChunk(chunk.body);
                    if (payload) {
                        setImages((previous) => {
                            const existing = previous[payload.id];
                            if (
                                existing &&
                                existing.base64 === payload.base64 &&
                                existing.mimeType === payload.mimeType &&
                                existing.hint === payload.hint &&
                                existing.source === payload.source
                            ) {
                                return previous;
                            }

                            return {
                                ...previous,
                                [payload.id]: payload
                            };
                        });
                        saveAssistantImage(payload);
                    }
                    continue;
                }

                if (chunk.contentType === 'error') {
                    setPendingStatus('');
                    const errorText = typeof chunk.body === 'string' ? chunk.body : JSON.stringify(chunk.body ?? {});
                    if (debugConsoleEnabledRef.current) {
                        console.error('[SKF Widget] Stream error:', errorText);
                    }
                    if (isAuthErrorPayload(errorText, chunk.body)) {
                        sawAuthError = true;
                        if (!hasShownAuthError) {
                            const resolvedMessage = resolveAuthErrorMessage(errorText, chunk.body);
                            emitAuthErrorEvent(resolvedMessage);
                            setHasShownAuthError(true);
                            insertAuthErrorMessage(resolvedMessage);
                        }
                        continue;
                    }
                    aggregatedResponse = errorText;
                    updateLatestAssistantMessage({ content: errorText });
                    continue;
                }

                if (chunk.type === 'text' && typeof chunk.body === 'string') {
                    aggregatedResponse += chunk.body;
                    updateLatestAssistantMessage({ content: aggregatedResponse });

                    if (chunk.body.trim().length > 0) {
                        setPendingStatus('');
                    }
                }
            }

            setPendingStatus('');

            if (!aggregatedResponse.length && !sawAuthError) {
                updateLatestAssistantMessage({ content: 'The assistant did not return any text.' });
            }
        } catch (error) {
            if (controller.signal.aborted) {
                wasCanceled = true;
                setPendingStatus('');
                updateLatestAssistantMessage({ content: 'Request canceled.' });
            } else {
                setPendingStatus('');
                const friendlyMessage = error instanceof Error ? error.message : 'Something unexpected happened.';
                if (debugConsoleEnabledRef.current) {
                    console.error('[SKF Widget] Request failed:', friendlyMessage, error);
                }
                updateLatestAssistantMessage({
                    content: `We could not complete your request: ${friendlyMessage}`
                });
            }
        } finally {
            setIsStreaming(false);
            abortControllerRef.current = null;
            isRequestInFlightRef.current = false;

            if (!wasCanceled) {
                updateLatestAssistantMessage({
                    durationMs: Math.max(0, getTimestampMs() - requestStart)
                });
            } else {
                updateLatestAssistantMessage({
                    durationMs: undefined
                });
            }
        }

        return true;
    }, [
        activeLanguage,
        chatService,
        hasShownAuthError,
        insertAuthErrorMessage,
        onLanguageOverride,
        setDraftMessage,
        threadId,
        updateLatestAssistantMessage
    ]);

    const sendMessage = useCallback(
        async (userImages?: UserImageUpload[]) => {
            await sendPreparedMessage(draftMessage, { userImages, clearDraftAfterSend: true });
        },
        [draftMessage, sendPreparedMessage]
    );

    const sendProgrammaticMessage = useCallback(
        (message: string): boolean => {
            if (isRequestInFlightRef.current) {
                return false;
            }

            if (!message.trim().length) {
                return false;
            }

            void sendPreparedMessage(message, { clearDraftAfterSend: false });
            return true;
        },
        [sendPreparedMessage]
    );

    const cancelStreaming = useCallback(() => {
        abortControllerRef.current?.abort();
        setPendingStatus('');
        lastLoggedStatusRef.current = '';
    }, []);

    const resetConversation = useCallback(() => {
        abortControllerRef.current?.abort();
        isRequestInFlightRef.current = false;
        setThreadId(null);
        setPendingStatus('');
        lastLoggedStatusRef.current = '';
        setHasShownAuthError(false);
        setDraftMessage('');
        setMessages([]);
        setImages({});
        setTaskPlan(null);
        setIsSubmittingFeedback(false);
        setConversationResponseCount(0);
        conversationResponseBufferRef.current.clear();
        clearAssistantImageCache();
    }, [chatService]);

    const copyMessage = useCallback(async (content: string) => {
        await copyTextToClipboard(content);
    }, []);

    const copyConversationJsonResponses = useCallback(async () => {
        const payload = conversationResponseBufferRef.current.toJson();
        await copyTextToClipboard(payload);
    }, []);

    const conversationId = useMemo(() => decodeConversationId(threadId), [threadId]);
    const canSubmitLatestFeedback = useMemo(() => {
        if (!feedbackService || isStreaming || isSubmittingFeedback || !conversationId) {
            return false;
        }

        for (let index = messages.length - 1; index >= 0; index -= 1) {
            const message = messages[index];
            if (message.role !== 'assistant') {
                continue;
            }

            if (!message.content.trim().length) {
                return false;
            }

            return !message.feedbackSubmitted;
        }

        return false;
    }, [conversationId, feedbackService, isStreaming, isSubmittingFeedback, messages]);

    const submitLatestFeedback = useCallback(async (input: SubmitFeedbackInput): Promise<boolean> => {
        if (!feedbackService || !conversationId || !canSubmitLatestFeedback) {
            return false;
        }

        let targetAssistantMessageId = '';
        setMessages((previous) => {
            const next = [...previous];
            for (let index = next.length - 1; index >= 0; index -= 1) {
                if (next[index].role !== 'assistant') {
                    continue;
                }

                if (!next[index].content.trim().length) {
                    break;
                }

                targetAssistantMessageId = next[index].id;
                next[index] = {
                    ...next[index],
                    feedbackSubmitted: true
                };
                break;
            }

            return next;
        });

        const request: SubmitFeedbackRequest = {
            conversationId,
            type: input.type,
            clientEventId: createUuid(),
            reason: input.reason,
            comment: input.comment?.trim()
        };

        setIsSubmittingFeedback(true);
        try {
            await feedbackService.submit(request, threadId);
            return true;
        } catch {
            if (targetAssistantMessageId) {
                setMessages((previous) =>
                    previous.map((message) =>
                        message.id === targetAssistantMessageId
                            ? {
                                  ...message,
                                  feedbackSubmitted: false
                              }
                            : message
                    )
                );
            }

            return false;
        } finally {
            setIsSubmittingFeedback(false);
        }
    }, [canSubmitLatestFeedback, conversationId, feedbackService, threadId]);

    return {
        messages,
        draftMessage,
        isStreaming,
        hasBackend: Boolean(chatService),
        pendingStatus,
        images,
        taskPlan,
        isDebugConsoleEnabled,
        conversationResponseCount,
        setDraftMessage,
        sendMessage,
        sendProgrammaticMessage,
        cancelStreaming,
        resetConversation,
        copyMessage,
        copyConversationJsonResponses,
        canSubmitLatestFeedback,
        isSubmittingFeedback,
        submitLatestFeedback
    };
}

function decodeConversationId(threadId: string | null): string {
    if (!threadId?.trim()) {
        return '';
    }

    try {
        const decoded = atob(threadId.trim());
        const separatorIndex = decoded.indexOf(':');
        if (separatorIndex <= 0) {
            return '';
        }

        return decoded.slice(0, separatorIndex).trim();
    } catch {
        return '';
    }
}

export const __taskPlanTestUtils = {
    normalizeTaskPlanChunk,
    mergeTaskPlanState,
    shouldClearTaskPlan
};
